import {
  Container,
  FillGradient,
  Graphics,
  Mesh,
  MeshGeometry,
  Texture,
} from "pixi.js";
import { penumbraGeometry, ShadowOutline } from "./shadowOutline";

/**
 * The shadow a piece throws on the surface under it — ambient
 * occlusion, one rule: height above the surface decides everything.
 * Every inch a piece stands off the table buys SHADOW_PX_PER_STAND_INCH
 * of rim, evenly all around; the same height lightens the shadow (a
 * riser blocks a shrinking share of the light dome) and IS the softness
 * — the penumbra spans the whole spread, so a piece near contact casts
 * a tight dark line and a tall one a wide pale pool. A 2x4 flat, on
 * edge, and on end wear three distinct shadows; picking a piece up
 * adds the carry's inches on top.
 *
 * The stand height is capped so a very tall piece (a board on end)
 * darkens a generous rim rather than half the bench; the carried lift
 * adds past the cap, so lifting always visibly spreads the pool.
 *
 * The shadow is the piece's own outline — a rough board's wavy sawn
 * edges and a mitered end's slant shade exactly the wood over them, not
 * a box around it. The core is the outline filled solid, and the
 * penumbra is one mesh fringing it (shadowOutline.ts): a quad off every
 * edge along its outward normal, a fan around every convex corner, with
 * the fade ramp riding the UVs (0 at the wood, 1 at the rim) over one
 * shared gradient texture. That gives the true falloff on any polygon,
 * at any spread, in one draw — not a blur filter (a render pass per
 * shadow across a floor of piles) and not stacked rings (they band).
 * Darkness rides the container's alpha, so nothing rebuilds for it.
 * Every material's outline and stand height live in materialShadow.ts;
 * pieces built from parts cast one shadow for the whole piece there.
 */

/** How much rim one inch of stand height buys. */
export const SHADOW_PX_PER_STAND_INCH = 1.5;

/** Stand height past which the resting rim stops growing. */
const SHADOW_STAND_CAP_IN = 6;

/** Darkness at contact; height fades it (see ContactShadow.draw). */
const CONTACT_ALPHA = 0.26;

/** Inches of height that cost the shadow half its contact darkness. */
const ALPHA_FADE_HEIGHT_IN = 6;

/** The penumbra's profile: full against the core, easing to nothing at
 * the rim — a quasi-gaussian falloff. */
const FADE_STOPS: ReadonlyArray<[number, number]> = [
  [0, 1],
  [0.5, 0.55],
  [0.8, 0.2],
  [1, 0],
];

/** The one fade ramp every penumbra samples: FADE_STOPS painted across
 * a 1-px-tall strip, black at u=0 fading to clear at u=1. */
let ramp: Texture | null = null;

function rampTexture(): Texture {
  if (ramp) return ramp;
  const gradient = new FillGradient({
    type: "linear",
    start: { x: 0, y: 0 },
    end: { x: 1, y: 0 },
    textureSpace: "local",
  });
  for (const [offset, alpha] of FADE_STOPS) {
    gradient.addColorStop(offset, `rgba(0,0,0,${alpha})`);
  }
  gradient.buildGradient();
  ramp = gradient.texture;
  return ramp;
}

/**
 * One piece's shadow: the outline's solid core under a mesh penumbra.
 * `draw` replaces whatever was drawn before — redraw it, don't scale
 * it, when the footprint or the height changes, so the fringe stays an
 * even band all the way round.
 */
export class ContactShadow extends Container {
  private readonly core = this.addChild(new Graphics());
  private readonly penumbra = this.addChild(
    new Mesh({
      geometry: new MeshGeometry({
        positions: new Float32Array(0),
        uvs: new Float32Array(0),
        indices: new Uint32Array(0),
      }),
      texture: rampTexture(),
    }),
  );

  constructor() {
    super();
    this.penumbra.visible = false;
  }

  draw(
    outline: ShadowOutline,
    standInches: number,
    options: {
      /** Inches the piece is held off the surface (the carried lift),
       * spreading and fading the pool past the resting cap. */
      liftInches?: number;
    } = {},
  ): void {
    const { liftInches = 0 } = options;
    const heightIn = Math.min(standInches, SHADOW_STAND_CAP_IN) + liftInches;
    const spread = heightIn * SHADOW_PX_PER_STAND_INCH;
    // Darkness lives on the container, not in the ramp, so the one
    // shared texture serves every height.
    this.alpha = CONTACT_ALPHA / (1 + heightIn / ALPHA_FADE_HEIGHT_IN);
    this.core.clear();
    this.core.poly(outline as number[]).fill(0x000000);
    const geometry = penumbraGeometry(outline, spread);
    this.penumbra.visible = geometry.indices.length > 0;
    if (!this.penumbra.visible) return;
    const mesh = this.penumbra.geometry;
    mesh.positions = geometry.positions;
    mesh.uvs = geometry.uvs;
    mesh.indices = geometry.indices;
  }

  clear(): void {
    this.core.clear();
    this.penumbra.visible = false;
  }
}
